import * as _ from 'lodash';
import { IMS_TYPE__ASSET } from 'app/shared/collections/asset';
import { IMS_TYPE__IP_DEVICE } from 'app/shared/collections/ip_device';
import { errorToText, IMSError } from 'app/shared/errors/generator';
import ImsObjectCollection from 'app/shared/collections/ims_object_collection.service';
import { UnitService } from 'app/shared/unit.service';

export default class UtilityService {
    /**
     * Used to process the standard API error object into text we return
     */
    static apiErrorIntoText(error: IMSError, collection: ImsObjectCollection = undefined): string {
        return errorToText(error, collection);
    }

    /**
     * Does the definitions we are using for normalization have the information
     * needed to normalize the given unit.
     *
     * Accepts a suffix after a forward slash (/).
     * E.g.
     *
     * kB/s will be accepted, using the same conversion maths as kB
     *
     * @param definitions Definitions dict
     * @param suffix string, unit to convert
     * @returns {boolean} true if we can normalize the unit
     */
    static shouldNormalizeUnit(definitions, suffix) {
        if (!suffix) {
            return false;
        }

        if (definitions?.values) {
            return !!definitions.values.find((u) => u.suffix === suffix);
        } else if (definitions?.suffix) {
            return definitions.suffix === suffix;
        }

        return false;
    }

    /**
     * Simplifies units and values
     * Eg:
     * 100000 kB -> 100 MB
     * @param definitions Probably provide a list from the Constant Service
     * @param value The value to normalize
     * @param unit The unit of the current value
     */
    static normalizeUnit(
        definitions,
        value,
        unit,
        aggregationType?: string,
    ): { value: string | number | Date; unit: string } {
        return UnitService.convertValue(definitions, value, unit, aggregationType);
    }

    /**
     * Simplifies units and values
     * Eg:
     * 100000 kB -> 100 MB
     * @param definitions Probably provide a list from the Constant Service
     * @param value The value to normalize
     * @param unit The unit of the current value
     */
    static normalizeUnitForChart(definitions, value, unit, decimals): string | number | Date {
        let v = UnitService.forceConvert(definitions, value, unit);

        if (typeof v === 'number') {
            v = v.toFixed(decimals);
        }

        return v;
    }

    /**
     * Calls normalize unit and then formats the result into a string. 'value units'
     * @param definitions
     * @param value
     * @param unit
     * @param pluralize
     * @param spaced
     * @returns {string}
     */
    static normalizeUnitToStr(
        definitions,
        value,
        unit,
        pluralize: boolean = false,
        spaced: boolean = true,
    ) {
        const result = UtilityService.normalizeUnit(definitions, value, unit);
        const space = spaced ? ' ' : '';
        const plural = pluralize && result.value !== 1 && !unit.endsWith('s') ? 's' : '';
        return `${result.value}${space}${result.unit}${plural}`;
    }

    static apiErrorOnClickFunction(error, imsItemPanelService) {
        if (error && error.data) {
            if (typeof error.data === 'object') {
                let onClick;
                for (const errorItemName in error.data) {
                    const errorItem = error.data[errorItemName];
                    if (_.isArray(errorItem)) {
                        for (let i = 0; i < errorItem.length; i++) {
                            if (errorItemName === 'conflicting_asset_id') {
                                const duplicatedId = parseInt(error.data.conflicting_asset_id[0]);
                                onClick = () =>
                                    imsItemPanelService.openOrFocus(IMS_TYPE__ASSET, duplicatedId);
                            }
                            if (errorItemName === 'conflicting_ip_device_id') {
                                const duplicatedId = parseInt(
                                    error.data.conflicting_ip_device_id[0],
                                );
                                onClick = () =>
                                    imsItemPanelService.openOrFocus(
                                        IMS_TYPE__IP_DEVICE,
                                        duplicatedId,
                                    );
                            }
                        }
                    }
                }
                return onClick;
            }
        }
    }
}
